/**
 * reorder — pure ProseMirror logic for swapping a node with its
 * previous / next sibling within the same parent.
 *
 * Used by:
 *   - The up/down arrow buttons in ReorderControls (mouse path)
 *   - The Alt+↑ / Alt+↓ keymap in the Reorder extension
 *     (keyboard path)
 *
 * Both paths converge on `moveNodeUp` / `moveNodeDown`, which:
 *   1. Resolve the position to (parent, index, node)
 *   2. Bail if there is no same-type sibling on that side. Same-
 *      type gating is what stops a topic from accidentally
 *      swapping past the meetingHeader / attendeesBlock /
 *      actionItemsBlock siblings sitting before it at the doc
 *      level.
 *   3. Build a single tr.replaceWith(from, to, [swapped pair])
 *      transaction and dispatch it.
 *
 * REORDERABLE_TYPES enumerates the node types that participate in
 * the keyboard shortcut. (The mouse path is wired explicitly per
 * NodeView, so it doesn't consult this set.)
 */
import { Extension } from '@tiptap/core';
import type { EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';

export const REORDERABLE_TYPES = new Set([
  'topic',
  'bullet',
  'actionItem',
  'attendeeGroup',
]);

interface ReorderTarget {
  parent: PMNode;
  index: number;
  node: PMNode;
}

/**
 * Resolve `pos` (which the caller obtained from a NodeView's
 * `getPos()` or by walking the selection's depth chain) to the
 * (parent, index, node) tuple needed to perform a swap. Returns
 * null if the position doesn't land on a real child boundary.
 */
export function findReorderTarget(
  view: EditorView,
  pos: number,
): ReorderTarget | null {
  const doc = view.state.doc;
  if (pos < 0 || pos >= doc.content.size) return null;
  const $pos = doc.resolve(pos);
  const parent = $pos.parent;
  const index = $pos.index();
  const node = parent.maybeChild(index);
  if (!node) return null;
  return { parent, index, node };
}

/**
 * Swap the node at `pos` with its previous sibling (same type
 * required). Returns true on success, false if blocked.
 *
 * Bullets get a special path: when a bullet is the FIRST bullet
 * inside its topic, "move up" migrates it to the END of the
 * previous topic's bullet list instead of bailing. That makes
 * Alt+↑ / the ⬆ button keep working past topic boundaries, which
 * matches how a user thinks about reordering meeting notes.
 */
export function moveNodeUp(view: EditorView, pos: number): boolean {
  const target = findReorderTarget(view, pos);
  if (!target) return false;
  const { parent, index, node } = target;

  // Cross-topic bullet migration — fires when the user is at the
  // top of a topic's bullet list and presses ⬆. The schema's
  // topicTitle sits at index 0, so the first bullet is at
  // index 1.
  if (
    node.type.name === 'bullet' &&
    parent.type.name === 'topic' &&
    index === 1
  ) {
    return moveBulletUpAcrossTopics(view, pos);
  }

  if (index === 0) return false;
  const prev = parent.child(index - 1);
  // Same-type gate: only swap with a sibling of the same node
  // type. Without this a topic at the top of the topics list
  // would try to swap with the actionItemsBlock above it, which
  // is structurally wrong.
  if (prev.type !== node.type) return false;

  const from = pos - prev.nodeSize;
  const to = pos + node.nodeSize;
  const tr = view.state.tr.replaceWith(from, to, [node, prev]);
  view.dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Swap the node at `pos` with its next sibling (same type
 * required). Returns true on success, false if blocked.
 *
 * Bullets get a special path: when a bullet is the LAST bullet
 * inside its topic, "move down" migrates it to the START of the
 * next topic's bullet list (right after that topic's title).
 */
export function moveNodeDown(view: EditorView, pos: number): boolean {
  const target = findReorderTarget(view, pos);
  if (!target) return false;
  const { parent, index, node } = target;

  // Cross-topic bullet migration — fires when the user is at the
  // bottom of a topic's bullet list and presses ⬇.
  if (
    node.type.name === 'bullet' &&
    parent.type.name === 'topic' &&
    index === parent.childCount - 1
  ) {
    return moveBulletDownAcrossTopics(view, pos);
  }

  if (index >= parent.childCount - 1) return false;
  const next = parent.child(index + 1);
  if (next.type !== node.type) return false;

  const from = pos;
  const to = pos + node.nodeSize + next.nodeSize;
  const tr = view.state.tr.replaceWith(from, to, [next, node]);
  view.dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Migrate a bullet to the END of the previous topic's bullets.
 * Caller must have verified the bullet is the FIRST bullet in
 * its current topic. Returns true on success.
 */
function moveBulletUpAcrossTopics(view: EditorView, pos: number): boolean {
  const doc = view.state.doc;
  const $pos = doc.resolve(pos);
  // $pos must be inside a topic at depth 1 (topic is a doc-level
  // child). If we're nested deeper (e.g. some future schema), bail
  // — the same-parent path will have already handled it.
  if ($pos.depth !== 1 || $pos.parent.type.name !== 'topic') return false;

  const topic = $pos.parent;
  const bulletIndex = $pos.index();
  const bulletNode = topic.child(bulletIndex);
  if (!bulletNode || bulletNode.type.name !== 'bullet') return false;

  // Find the nearest previous topic at the doc level.
  const topicIndex = $pos.index(0);
  let prevTopicIdx = -1;
  for (let i = topicIndex - 1; i >= 0; i--) {
    if (doc.child(i).type.name === 'topic') {
      prevTopicIdx = i;
      break;
    }
  }
  if (prevTopicIdx < 0) return false;

  const prevTopic = doc.child(prevTopicIdx);
  // posAtIndex(prevTopicIdx, 0) — position right BEFORE prevTopic
  // at the doc level. Adding prevTopic.nodeSize lands at the
  // position right AFTER prevTopic. Subtracting 1 puts us INSIDE
  // prevTopic at the END of its content, which is exactly where
  // a new last-bullet should land.
  const prevTopicStart = $pos.posAtIndex(prevTopicIdx, 0);
  const insertPos = prevTopicStart + prevTopic.nodeSize - 1;

  const deleteFrom = pos;
  const deleteTo = pos + bulletNode.nodeSize;

  // insertPos sits BEFORE deleteFrom (prev topic comes first in
  // doc order), so deleting later doesn't shift insertPos.
  const tr = view.state.tr
    .delete(deleteFrom, deleteTo)
    .insert(insertPos, bulletNode);
  view.dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Migrate a bullet to the START of the next topic's bullets
 * (right after the next topic's title). Caller must have verified
 * the bullet is the LAST bullet in its current topic.
 */
function moveBulletDownAcrossTopics(view: EditorView, pos: number): boolean {
  const doc = view.state.doc;
  const $pos = doc.resolve(pos);
  if ($pos.depth !== 1 || $pos.parent.type.name !== 'topic') return false;

  const topic = $pos.parent;
  const bulletIndex = $pos.index();
  const bulletNode = topic.child(bulletIndex);
  if (!bulletNode || bulletNode.type.name !== 'bullet') return false;

  // Find the nearest next topic at the doc level.
  const topicIndex = $pos.index(0);
  let nextTopicIdx = -1;
  for (let i = topicIndex + 1; i < doc.childCount; i++) {
    if (doc.child(i).type.name === 'topic') {
      nextTopicIdx = i;
      break;
    }
  }
  if (nextTopicIdx < 0) return false;

  const nextTopic = doc.child(nextTopicIdx);
  const topicTitle = nextTopic.child(0);
  // posAtIndex(nextTopicIdx, 0) — doc-level position right BEFORE
  // nextTopic. +1 enters nextTopic. + topicTitle.nodeSize skips
  // past the title to land at the position where the first
  // bullet would sit.
  const nextTopicStart = $pos.posAtIndex(nextTopicIdx, 0);
  const insertPos = nextTopicStart + 1 + topicTitle.nodeSize;

  const deleteFrom = pos;
  const deleteTo = pos + bulletNode.nodeSize;

  // insertPos sits AFTER deleteFrom (next topic comes later in
  // doc order), so deleting first shifts the insertion target by
  // -(deleteTo - deleteFrom). Compensate by subtracting the
  // shift before passing to insert().
  const shift = deleteTo - deleteFrom;
  const tr = view.state.tr
    .delete(deleteFrom, deleteTo)
    .insert(insertPos - shift, bulletNode);
  view.dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Whether the node at `pos` can move up / down. Used by
 * ReorderControls to grey out the arrow buttons at the
 * boundaries of a parent's child list.
 *
 * Bullets get extra-permissive bounds: a first/last bullet is
 * still reorderable if there's another topic on the other side
 * to receive it (cross-topic migration).
 */
export function reorderBounds(
  view: EditorView,
  pos: number,
): { canMoveUp: boolean; canMoveDown: boolean } {
  const target = findReorderTarget(view, pos);
  if (!target) return { canMoveUp: false, canMoveDown: false };
  const { parent, index, node } = target;

  // Standard same-parent bounds.
  const prev = index > 0 ? parent.child(index - 1) : null;
  const next =
    index < parent.childCount - 1 ? parent.child(index + 1) : null;
  let canMoveUp = prev != null && prev.type === node.type;
  let canMoveDown = next != null && next.type === node.type;

  // Cross-topic bullet bounds. If the bullet is at the top/bottom
  // of its topic, the button is still enabled if there's another
  // topic on the corresponding side to migrate to.
  if (node.type.name === 'bullet' && parent.type.name === 'topic') {
    const doc = view.state.doc;
    const $pos = doc.resolve(pos);
    if ($pos.depth === 1) {
      const topicIndex = $pos.index(0);
      if (!canMoveUp && index === 1) {
        // First bullet — look for a previous topic.
        for (let i = topicIndex - 1; i >= 0; i--) {
          if (doc.child(i).type.name === 'topic') {
            canMoveUp = true;
            break;
          }
        }
      }
      if (!canMoveDown && index === parent.childCount - 1) {
        // Last bullet — look for a next topic.
        for (let i = topicIndex + 1; i < doc.childCount; i++) {
          if (doc.child(i).type.name === 'topic') {
            canMoveDown = true;
            break;
          }
        }
      }
    }
  }

  return { canMoveUp, canMoveDown };
}

/* ─── Tiptap extension ──────────────────────────────────────────────── */

/**
 * Walk the selection's depth chain looking for the innermost node
 * whose type is in REORDERABLE_TYPES (topic, bullet, actionItem,
 * attendeeGroup) and move it. If none of the ancestors is
 * reorderable, returns false so the keymap falls through.
 *
 * Used by the Alt+↑ / Alt+↓ keyboard shortcuts. The user's
 * selection is most often inside editable text inside the
 * reorderable block (a bullet's body, an action item's
 * description, etc.) — walking up the chain finds the outer
 * container regardless of where the cursor sits within it.
 */
function moveAtSelection(view: EditorView, dir: 'up' | 'down'): boolean {
  const { $from } = view.state.selection;
  for (let d = $from.depth; d >= 1; d--) {
    const node = $from.node(d);
    if (REORDERABLE_TYPES.has(node.type.name)) {
      const pos = $from.before(d);
      return dir === 'up'
        ? moveNodeUp(view, pos)
        : moveNodeDown(view, pos);
    }
  }
  return false;
}

/**
 * Reorder — Tiptap extension that wires Alt+↑ / Alt+↓ to the
 * `moveNodeUp` / `moveNodeDown` helpers above. Mirrors the
 * mouse-path up/down arrow buttons rendered by ReorderControls.
 */
export const Reorder = Extension.create({
  name: 'reorder',
  addKeyboardShortcuts() {
    return {
      'Alt-ArrowUp': () => moveAtSelection(this.editor.view, 'up'),
      'Alt-ArrowDown': () => moveAtSelection(this.editor.view, 'down'),
    };
  },
});
